import * as MixpanelProfilePageActions from '@analytics/mixpanel/actions/ProfilePage';
import GreenCheck from '@assets/check_circle';
import FormElement from '@components/Form/Element/FormElement';
import { useYelaForm } from '@components/Form/Form';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import {
  FIRST_TO_KNOW,
  I18N_PROFILE_CONTEXT,
  NOTIFIED,
  NOTIFY_ME,
  NOTIFY_ME_BUTTON,
  NOTIFY_ME_ERROR,
  NOTIFY_ME_LABEL,
  NOTIFY_ME_PLACEHOLDER,
} from '../ProfileForm.constants';
import { NotifyMeForm, TNotifyMeForm } from '../ProfileForm.types';
import {
  NotifyMeContainer,
  NotifyMeDivider,
  NotifyMeFooterText,
  NotifyMeLoggedInButton,
  NotifyMeLoggedInFormWrapper,
  NotifyMeLoggedOutButton,
  NotifyMeLoggedOutFormWrapper,
  NotifyMeLoggedOutInput,
  NotifyMeLoggedOutInputWrapper,
  NotifyMeLoggedOutLabel,
  NotifyMeNotifiedInfo,
  NotifyMeNotifiedInfoText,
} from './NotifyMe.styles';
import { LoggedInProps, LoggedOutProps, NotifyMeProps } from './NotifyMe.types';

const LoggedIn = ({ activeTab, slug, loading, notified, onClick }: LoggedInProps): JSX.Element => {
  const { t } = useTranslation(I18N_PROFILE_CONTEXT);
  const router = useRouter();

  const onSubmitClick = () => {
    MixpanelProfilePageActions.onNotifyMeButtonClicked({
      router,
      details: {
        messageTypes: [activeTab],
        celebrityName: slug,
      },
    });
    onClick();
  };

  return (
    <NotifyMeContainer center={true}>
      {!notified && (
        <NotifyMeLoggedInFormWrapper>
          <NotifyMeLoggedInButton text={t(NOTIFY_ME)} uppercase onClick={onSubmitClick} loadingState={loading} />
        </NotifyMeLoggedInFormWrapper>
      )}
      {notified && (
        <NotifyMeNotifiedInfo>
          <GreenCheck />
          <NotifyMeNotifiedInfoText>{t(NOTIFIED)}</NotifyMeNotifiedInfoText>
        </NotifyMeNotifiedInfo>
      )}
      <NotifyMeDivider />
      <NotifyMeFooterText>{t(FIRST_TO_KNOW)}</NotifyMeFooterText>
    </NotifyMeContainer>
  );
};

const LoggedOut = ({ activeTab, slug, loading, creatorName, notified, onClick }: LoggedOutProps): JSX.Element => {
  const {
    trigger,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useYelaForm<TNotifyMeForm>(NotifyMeForm);
  const { t } = useTranslation(I18N_PROFILE_CONTEXT);
  const router = useRouter();

  const onFormSubmit = async () => {
    await trigger();

    if (errors === undefined || errors == {}) return;

    onClick(getValues('email'));
  };

  const onSubmitClick = () => {
    MixpanelProfilePageActions.onJoinNotifyListButtonClicked({
      router,
      details: {
        messageType: activeTab,
        celebrityName: slug,
      },
    });
    const emailValue = getValues('email');
    if (errors.email || emailValue == '') {
      MixpanelProfilePageActions.onJoinNotifyListFailed({
        router,
        details: {
          messageType: activeTab,
          celebrityName: slug,
          failureInfo: { type: 'Invalid Field' },
        },
      });
    }
  };

  return (
    <NotifyMeContainer center={false}>
      {notified && (
        <NotifyMeNotifiedInfo>
          <GreenCheck />
          <NotifyMeNotifiedInfoText>{t(NOTIFIED)}</NotifyMeNotifiedInfoText>
        </NotifyMeNotifiedInfo>
      )}
      {!notified && (
        <>
          <NotifyMeLoggedOutLabel>{t(NOTIFY_ME_LABEL, { celebrityName: creatorName })}</NotifyMeLoggedOutLabel>
          <NotifyMeLoggedOutFormWrapper onSubmit={handleSubmit(onFormSubmit)} noValidate={true}>
            <NotifyMeLoggedOutInputWrapper>
              <FormElement hasError={!!errors.email} errorText={t(NOTIFY_ME_ERROR)}>
                <NotifyMeLoggedOutInput
                  name='email'
                  type='email'
                  register={register}
                  placeholder={t(NOTIFY_ME_PLACEHOLDER)}
                  hasError={!!errors.email}
                />
              </FormElement>
            </NotifyMeLoggedOutInputWrapper>
            <NotifyMeLoggedOutButton uppercase text={t(NOTIFY_ME_BUTTON)} type='submit' loadingState={loading} onClick={onSubmitClick} />
          </NotifyMeLoggedOutFormWrapper>
        </>
      )}
    </NotifyMeContainer>
  );
};

export const NotifyMe = ({ ...props }: NotifyMeProps): JSX.Element => {
  const [loading, setLoading] = useState(false);

  const withLoading = (func: () => Promise<void>) => {
    if (loading) return;
    setLoading(true);
    func().finally(() => setLoading(false));
  };

  if (!props.isLoggedIn)
    return (
      <LoggedOut
        loading={loading}
        notified={props.isNotified}
        {...props}
        onClick={email => {
          withLoading(() => props.notifyMe(email));
        }}
      />
    );

  return (
    <LoggedIn
      loading={loading}
      notified={props.isNotified}
      {...props}
      onClick={() => {
        withLoading(() => props.notifyMe());
      }}
    />
  );
};
